import type { FieldValues, Path } from 'react-hook-form';
import type { FormFieldBinding } from './binding';
import { renderTextField, type TextFieldOptions } from './textField';
import { renderSuggestField, type SuggestFieldOptions } from './suggestField';
import {
    renderSelectField,
    type SelectFieldOptions,
    type SelectOption
} from './selectField';
import {
    renderJoinedMultiSelectField,
    renderMultiSelectField,
    type MultiSelectOptions
} from './multiSelectField';
import {
    renderRichTextField,
    type RichTextFieldOptions
} from './richTextField';
import {
    renderPersonField,
    renderPersonMultiField,
    renderPersonMultiJoinedField
} from './personField';
import type { PersonPickerConfig } from '../personPicker';

/** Per-field options of a person picker. */
interface PersonFieldOptions {
    /** A Grabungsleiter field: the marked accounts group first. */
    leads?: boolean;
    /**
     * Several people only: why a chip is to be looked at, or null — it is
     * then drawn red with that as its tooltip (e.g. already billed).
     */
    flag?: (name: string) => string | null;
}

/**
 * The field renderers of ONE record form, bound to it.
 *
 * Every record dialog used to carry its own `text()`/`suggest()` closures —
 * nine of them, differing only in which label catalog they read. Destructure
 * what the form needs and its call sites stay as terse as they were:
 *
 *     const { text, suggest } = bindFormFields<FormValues>({ … });
 *     …
 *     {text('length')}
 *     {suggest('excavator', crewNames)}
 *
 * A plain function, not a hook: it holds no state, and the closures it returns
 * are called during the SAME render that made them. Nothing memoizes on their
 * identity, so rebuilding them each render costs nothing and keeps the errors
 * they read current.
 */
export function bindFormFields<Values extends FieldValues>(
    binding: FormFieldBinding<Values>
) {
    return {
        /** A registered text field. */
        text: (name: Path<Values>, options?: TextFieldOptions) =>
            renderTextField(binding, name, options),
        /** Free text that offers what has been typed before. */
        suggest: (
            name: Path<Values>,
            options: readonly string[],
            fieldOptions?: SuggestFieldOptions
        ) => renderSuggestField(binding, name, options, fieldOptions),
        /**
         * ONE person, picked from the app's accounts — never free text. The
         * config comes from `usePersonPicker()`, held once by the form.
         */
        person: (
            name: Path<Values>,
            config: PersonPickerConfig,
            options?: PersonFieldOptions
        ) => renderPersonField(binding, name, config, options),
        /** Several people over a real `string[]` field. */
        personMulti: (
            name: Path<Values>,
            config: PersonPickerConfig,
            options?: PersonFieldOptions
        ) => renderPersonMultiField(binding, name, config, options),
        /** Several people over a field that stores them ', '-joined. */
        personMultiJoined: (
            name: Path<Values>,
            config: PersonPickerConfig,
            options?: PersonFieldOptions
        ) => renderPersonMultiJoinedField(binding, name, config, options),
        /** Chips over a `string[]` field. */
        multiSelect: (
            name: Path<Values>,
            options: readonly string[],
            fieldOptions?: MultiSelectOptions
        ) => renderMultiSelectField(binding, name, options, fieldOptions),
        /** Chips over a field that stores them ', '-joined. */
        multiSelectJoined: (
            name: Path<Values>,
            options: readonly string[],
            fieldOptions?: MultiSelectOptions
        ) => renderJoinedMultiSelectField(binding, name, options, fieldOptions),
        /** A closed list of choices — a link or a state, never free text. */
        select: (
            name: Path<Values>,
            options: readonly SelectOption[],
            fieldOptions?: SelectFieldOptions
        ) => renderSelectField(binding, name, options, fieldOptions),
        /** A long-text field with Textbausteine and Diktat. */
        richText: (name: Path<Values>, options?: RichTextFieldOptions) =>
            renderRichTextField(binding, name, options)
    };
}
